<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  list: { type: Object, default: null },
  boardId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const isLoading = ref(false);

const formState = reactive({ title: '', webhookUrl: '' });

const v$ = useVuelidate(
  {
    title: {
      required: helpers.withMessage(
        () => t('KANBAN.SETTINGS.LIST_FORM.ERRORS.TITLE'),
        required
      ),
    },
  },
  formState
);

const isEdit = computed(() => !!props.list);

const resetForm = () => {
  formState.title = props.list?.title || '';
  formState.webhookUrl = props.list?.webhook_url || '';
  v$.value.$reset();
};

watch(() => props.list, resetForm, { immediate: true });

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isLoading.value = true;
  try {
    if (isEdit.value) {
      await store.dispatch('kanban/lists/update', {
        id: props.list.id,
        title: formState.title,
        webhook_url: formState.webhookUrl,
      });
      useAlert(t('KANBAN.SETTINGS.LIST_API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('kanban/lists/create', {
        kanban_board_id: props.boardId,
        title: formState.title,
        webhook_url: formState.webhookUrl,
      });
      useAlert(t('KANBAN.SETTINGS.LIST_API.CREATE_SUCCESS'));
    }
    dialogRef.value.close();
  } catch (error) {
    useAlert(
      t(
        isEdit.value
          ? 'KANBAN.SETTINGS.LIST_API.UPDATE_ERROR'
          : 'KANBAN.SETTINGS.LIST_API.CREATE_ERROR'
      )
    );
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="resetForm"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <Input
        id="list-title"
        v-model="formState.title"
        :label="t('KANBAN.SETTINGS.LIST_FORM.TITLE_LABEL')"
        :placeholder="t('KANBAN.SETTINGS.LIST_FORM.TITLE_PLACEHOLDER')"
        :message="v$.title.$error ? v$.title.$errors[0]?.$message : ''"
        :message-type="v$.title.$error ? 'error' : 'info'"
        @blur="v$.title.$touch()"
      />
      <Input
        id="list-webhook-url"
        v-model="formState.webhookUrl"
        :label="t('KANBAN.SETTINGS.LIST_FORM.WEBHOOK_LABEL')"
        :placeholder="t('KANBAN.SETTINGS.LIST_FORM.WEBHOOK_PLACEHOLDER')"
        :message="t('KANBAN.SETTINGS.LIST_FORM.WEBHOOK_HINT')"
        message-type="info"
      />
      <div class="flex items-center justify-end gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="t('KANBAN.SETTINGS.LIST_FORM.CANCEL')"
          @click="dialogRef.close()"
        />
        <NextButton
          type="submit"
          :label="
            t(
              isEdit
                ? 'KANBAN.SETTINGS.LIST_FORM.UPDATE'
                : 'KANBAN.SETTINGS.LIST_FORM.CREATE'
            )
          "
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
